import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaPlus, FaArrowLeft, FaVideo, FaUpload } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { addReels, updateReels, listReels } from "../../Redux/Actions/propertiesResaleActions.jsx";
import toast from "react-hot-toast";
import "./addVideo.css";
import {
    completeMultipartUpload,
    generatePreSignedUrls,
    s3MultipartUploadStart,
    s3Upload,
    splitFileIntoChunks,
} from "../../Utils/s3Upload.jsx";

const VideoModal = ({ propertyId: propPropertyId, onClose }) => {
    const dispatch = useDispatch();
    const { reelsLoading, reelsList } = useSelector((state) => state.projects || {});

    const [mode, setMode] = useState("view");
    const [builderVideos, setBuilderVideos] = useState({});
    const [selectedBusinessType, setSelectedBusinessType] = useState("resale");
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [playingIndex, setPlayingIndex] = useState(null);
    const [editingReelId, setEditingReelId] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [capturing, setCapturing] = useState(false);

    const imageInputRef = useRef(null);
    const videoPreviewRef = useRef(null);

    const propertyId = propPropertyId;
    const reels = reelsList?.data || [];

    useEffect(() => {
        if (mode === "view" && propertyId) {
            dispatch(listReels({ page: 1, limit: 100, property: propertyId }));
        }
    }, [mode, dispatch, propertyId]);

    const handleRemoveImage = (e) => {
        e.preventDefault();
        setBuilderVideos((prev) => ({ ...prev, video: "", key: "" }));
        setUploadedFileName("");
    };

    const handleBack = () => {
        if (mode === "view") {
            onClose();
        } else {
            setMode("view");
            setBuilderVideos({});
            setEditingReelId(null);
            setSelectedBusinessType("resale");
            setUploadedFileName("");
        }
    };

    const handleScroll = (e) => {
        e.stopPropagation();
    };

    const handleVideoInput = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            toast.error("No file selected");
            return;
        }

        const sanitizedFileName = file.name.replace(/\s+/g, "_");
        const videoElement = document.createElement("video");
        videoElement.preload = "metadata";

        videoElement.onloadedmetadata = async () => {
            window.URL.revokeObjectURL(videoElement.src);
            const duration = videoElement.duration;

            if (file.size > 10 * 1024 * 1024) {
                toast.error("Video size must be 10 MB or less.");
                return;
            }

            if (duration > 300) {
                toast.error("Only videos up to 5 minutes are allowed.");
                setUploading(false);
                setUploadProgress(0);
                return;
            }

            try {
                setUploading(true);
                setUploadProgress(0);
                setUploadedFileName(file.name);

                const key = sanitizedFileName;
                const uploadResult = await s3MultipartUploadStart(key);
                const uploadId = uploadResult?.data;
                const chunks = splitFileIntoChunks(file);
                const chunkCount = chunks.length;

                const presignedUrls = await generatePreSignedUrls(key, uploadId, chunkCount);
                const urls = presignedUrls?.data;

                const parts = [];
                for (let i = 0; i < chunkCount; i++) {
                    const chunk = chunks[i];
                    const url = urls[i];
                    const uploadRes = await fetch(url, { method: "PUT", body: chunk });

                    if (!uploadRes.ok) {
                        toast.error(`Failed to upload chunk ${i + 1}`);
                        return;
                    }

                    const etag = uploadRes.headers.get("ETag");
                    const rawEtag = etag.replace(/^"(.+)"$/, "$1");
                    parts.push({ ETag: rawEtag, PartNumber: i + 1 });
                    setUploadProgress(Math.round(((i + 1) / chunkCount) * 100));
                }

                await completeMultipartUpload(key, uploadId, parts);
                const fileUrl = urls[0].split("?")[0];

                setBuilderVideos({ video: fileUrl, key, thumbnail: "" });
            } catch {
                toast.error("Failed to upload video.");
            } finally {
                setUploading(false);
                setUploadProgress(0);
            }
        };

        videoElement.onerror = () => {
            toast.error("Failed to read video metadata.");
        };

        videoElement.src = URL.createObjectURL(file);
    };

    const handleCaptureFrame = async () => {
        const video = videoPreviewRef.current;
        if (!video) return;

        try {
            setCapturing(true);
            const currentTime = video.currentTime;
            const response = await fetch(video.src, { mode: "cors" });
            const blob = await response.blob();
            const localUrl = URL.createObjectURL(blob);

            const tempVideo = document.createElement("video");
            tempVideo.crossOrigin = "anonymous";
            tempVideo.preload = "auto";
            tempVideo.src = localUrl;

            tempVideo.onloadedmetadata = () => {
                tempVideo.currentTime = currentTime;
            };

            tempVideo.onseeked = async () => {
                const canvas = document.createElement("canvas");
                canvas.width = tempVideo.videoWidth;
                canvas.height = tempVideo.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
                URL.revokeObjectURL(localUrl);

                canvas.toBlob(async (capturedBlob) => {
                    if (!capturedBlob) {
                        toast.error("Failed to capture frame");
                        setCapturing(false);
                        return;
                    }
                    const file = new File([capturedBlob], "thumbnail.jpg", { type: "image/jpeg" });
                    const s3Link = await s3Upload(file, dispatch);
                    setCapturing(false);
                    if (!s3Link) { toast.error("Failed to upload thumbnail"); return; }
                    setBuilderVideos((prev) => ({ ...prev, thumbnail: s3Link }));
                    toast.success("Thumbnail captured!");
                }, "image/jpeg", 0.92);
            };

            tempVideo.onerror = () => {
                URL.revokeObjectURL(localUrl);
                setCapturing(false);
                toast.error("Failed to load video for frame capture");
            };

            tempVideo.load();
        } catch (err) {
            setCapturing(false);
            toast.error("Failed to capture frame. Check S3 CORS settings.");
            console.error(err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        console.log("IDDDD+>>>>>", propertyId);
        if (!propertyId) {
            toast.error("Property id is required");
            return;
        }

        const payload = {
            property: propertyId,
            business_type: selectedBusinessType,
            videos: [
                {
                    video: builderVideos.video || "",
                    key: builderVideos.key || (builderVideos.video ? builderVideos.video.split("/").pop() : ""),
                    thumbnail: builderVideos.thumbnail || "",
                },
            ],
        };

        if (editingReelId) {
            const result = await dispatch(updateReels({ id: editingReelId, ...payload }));
            if (result) {
                setMode("view");
                setBuilderVideos({});
                setEditingReelId(null);
                setSelectedBusinessType("resale");
                setUploadedFileName("");
                dispatch(listReels({ page: 1, limit: 100, property: propertyId }));
            }
        } else {
            const result = await dispatch(addReels(payload));
            if (result) {
                setMode("view");
                setBuilderVideos({});
                setSelectedBusinessType("resale");
                setUploadedFileName("");
                dispatch(listReels({ page: 1, limit: 100, property: propertyId }));
            }
        }
    };

    const handleDeleteReel = async (reelId) => {
        const reel = reels.find((r) => r._id === reelId);
        if (!reel) return;

        const result = await dispatch(updateReels({ id: reelId, videos: [] }));
        if (result) {
            dispatch(listReels({ page: 1, limit: 100, property: propertyId }));
        }
    };

    return ReactDOM.createPortal(
        <div className="vmp-overlay" onClick={onClose}>
            <div className="vmp-modal" onClick={(e) => e.stopPropagation()} onWheel={handleScroll}>

                {/* HEADER */}
                <div className="vmp-header">
                    <div className="vmp-header-top">
                        <div className="vmp-header-left">
                            <div className="vmp-header-icon">🎬</div>
                            <div>
                                <p className="vmp-header-title">Property Reels</p>
                                <p className="vmp-header-sub">
                                    {mode === "view"
                                        ? "Manage your reels collection"
                                        : editingReelId
                                            ? "Update reel · Edit video & thumbnail"
                                            : "Upload video · Pick thumbnail · Save"}
                                </p>
                            </div>
                        </div>
                        <div className="vmp-header-right">
                            {mode === "edit" && (
                                <button
                                    onClick={handleBack}
                                    disabled={reelsLoading || uploading}
                                    className="vmp-btn-back"
                                >
                                    <FaArrowLeft size={10} /> Back
                                </button>
                            )}
                            {mode === "view" && (
                                <button
                                    disabled={reelsLoading || uploading}
                                    onClick={() => setMode("edit")}
                                    className="vmp-btn-add"
                                >
                                    <FaPlus size={10} /> Add New Reel
                                </button>
                            )}
                            <button onClick={onClose} className="vmp-btn-close">×</button>
                        </div>
                    </div>
                </div>

                {/* BODY */}
                <div className="vmp-body">
                    <input
                        type="file"
                        accept="video/*"
                        ref={imageInputRef}
                        onChange={handleVideoInput}
                        hidden
                        disabled={reelsLoading || uploading}
                    />

                    {mode === "edit" ? (
                        <div className="vmp-edit-body">

                            {/* STEP 1 — upload zone */}
                            {!builderVideos.video && !uploading && (
                                <div className="vmp-upload-zone" onClick={() => imageInputRef.current.click()}>
                                    <div className="vmp-upload-icon">
                                        <FaVideo size={22} color="white" />
                                    </div>
                                    <p className="vmp-upload-title">Drop your video here</p>
                                    <p className="vmp-upload-sub">or click to browse from your device</p>
                                    <div className="vmp-upload-badges">
                                        <span className="vmp-badge">MP4</span>
                                        <span className="vmp-badge">MOV</span>
                                        <span className="vmp-badge-muted">· Max 10MB · 5 min</span>
                                    </div>
                                    <button
                                        className="vmp-btn-dark"
                                        onClick={(e) => { e.stopPropagation(); imageInputRef.current.click(); }}
                                    >Choose File</button>
                                </div>
                            )}

                            {/* STEP 1 — uploading progress */}
                            {uploading && (
                                <div className="vmp-upload-progress-box">
                                    <div className="vmp-upload-progress-icon">
                                        <FaUpload size={22} color="white" />
                                    </div>
                                    <p className="vmp-upload-progress-label">Uploading video...</p>
                                    <p className="vmp-upload-progress-pct">{uploadProgress}%</p>
                                    <div className="vmp-progress-track">
                                        <div className="vmp-progress-fill" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                    <p className="vmp-upload-progress-note">Please don't close this window</p>
                                </div>
                            )}

                            {/* STEP 1 done — success bar */}
                            {builderVideos.video && (
                                <div className="vmp-success-bar">
                                    <div className="vmp-success-icon">✓</div>
                                    <div className="vmp-success-text">
                                        <p className="vmp-success-title">Video uploaded</p>
                                        <p className="vmp-success-filename">
                                            {uploadedFileName || builderVideos.video.split("/").pop()}
                                        </p>
                                    </div>
                                    <button
                                        className="vmp-btn-ghost"
                                        onClick={() => { setBuilderVideos({}); setUploadedFileName(""); imageInputRef.current.click(); }}
                                        disabled={reelsLoading || uploading}
                                    >Replace</button>
                                </div>
                            )}

                            {/* STEP 2 — video player with capture button */}
                            {builderVideos.video && (
                                <div className="vmp-video-section">
                                    <div className="vmp-video-wrapper">
                                        <video
                                            ref={videoPreviewRef}
                                            crossOrigin="anonymous"
                                            src={builderVideos.video}
                                            controls
                                            className="vmp-video-player"
                                        />
                                        <div className="vmp-capture-btn-wrap">
                                            <button
                                                onClick={handleCaptureFrame}
                                                disabled={reelsLoading || uploading || capturing}
                                                className={`vmp-capture-btn${capturing ? " vmp-capture-btn--loading" : ""}`}
                                            >
                                                {capturing ? (
                                                    <>
                                                        <span className="vmp-spinner" />
                                                        Capturing…
                                                    </>
                                                ) : (
                                                    <>📸 {builderVideos.thumbnail ? "Recapture frame" : "Capture this frame"}</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <p className="vmp-capture-hint">
                                        Pause at any moment → tap <strong>Capture this frame</strong>
                                    </p>
                                </div>
                            )}

                            {/* STEP 2 done — thumbnail bar */}
                            {builderVideos.thumbnail && (
                                <div className="vmp-thumb-bar">
                                    <img src={builderVideos.thumbnail} alt="thumbnail" className="vmp-thumb-preview" />
                                    <div className="vmp-thumb-text">
                                        <p className="vmp-thumb-title">Thumbnail captured</p>
                                        <p className="vmp-thumb-sub">Tap recapture to change it</p>
                                    </div>
                                    <button
                                        className="vmp-btn-ghost"
                                        onClick={() => setBuilderVideos((prev) => ({ ...prev, thumbnail: "" }))}
                                        disabled={reelsLoading || uploading}
                                    >Remove</button>
                                </div>
                            )}

                            {/* STEP 3 — business type select */}
                            <div className="vmp-business-section">
                                <label className="vmp-label">Business Type</label>
                                <select
                                    className="vmp-select"
                                    value={selectedBusinessType}
                                    onChange={(e) => setSelectedBusinessType(e.target.value)}
                                    disabled={reelsLoading || uploading}
                                >
                                    <option value="resale">Resale</option>
                                    <option value="business">Business</option>
                                </select>
                            </div>

                            {/* ACTION BUTTONS */}
                            <div className="vmp-actions">
                                <button
                                    onClick={(e) => { e.preventDefault(); handleBack(); }}
                                    disabled={reelsLoading || uploading}
                                    className="vmp-btn-cancel"
                                >Cancel</button>
                                <button
                                    onClick={handleSave}
                                    disabled={reelsLoading || uploading || !builderVideos.video || !builderVideos.thumbnail}
                                    className={`vmp-btn-save${(reelsLoading || uploading || !builderVideos.video || !builderVideos.thumbnail) ? " vmp-btn-save--disabled" : ""}`}
                                >
                                    ☁️ {reelsLoading ? "Saving..." : editingReelId ? "Update Reel" : "Save Reel"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="vmp-grid">
                            {reels?.length ? (
                                reels.map((reel, reelIndex) =>
                                    reel?.videos?.map((video, videoIndex) => (
                                        <div key={`${reelIndex}-${videoIndex}`} className="vmp-reel-card">
                                            <div className="vmp-reel-media">
                                                {playingIndex === `${reelIndex}-${videoIndex}` ? (
                                                    <video
                                                        src={video.video}
                                                        className="vmp-reel-video"
                                                        controls
                                                        autoPlay
                                                        onEnded={() => setPlayingIndex(null)}
                                                    />
                                                ) : (
                                                    <>
                                                        <img src={video.thumbnail} alt="thumbnail" className="vmp-reel-thumb" />
                                                        <button
                                                            onClick={() => setPlayingIndex(`${reelIndex}-${videoIndex}`)}
                                                            className="vmp-play-btn"
                                                        >▶</button>
                                                    </>
                                                )}
                                                <div className="vmp-reel-actions">
                                                    <button
                                                        onClick={() => {
                                                            setEditingReelId(reel._id);
                                                            setBuilderVideos({
                                                                video: video.video,
                                                                key: video.key,
                                                                thumbnail: video.thumbnail,
                                                            });
                                                            setUploadedFileName(video.video.split("/").pop());
                                                            setSelectedBusinessType(reel.business_type || "resale");
                                                            setMode("edit");
                                                        }}
                                                        disabled={reelsLoading || uploading}
                                                        className="vmp-reel-action-btn"
                                                    >✎</button>
                                                    <button
                                                        onClick={() => handleDeleteReel(reel._id)}
                                                        disabled={reelsLoading || uploading}
                                                        className="vmp-reel-action-btn"
                                                    >×</button>
                                                </div>
                                                {reel.business_type && (
                                                    <div className="vmp-reel-type-badge">
                                                        {reel.business_type}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )
                            ) : (
                                <div className="vmp-empty">
                                    <p className="vmp-empty-icon">🎬</p>
                                    <p className="vmp-empty-title">No reels found for this property.</p>
                                    <p className="vmp-empty-sub">Add your first reel to get started</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default VideoModal;